import React from 'react';
import { GenericWidgetProps } from '../ReportcenterWidget';
import { AssistChip, Block, Colors, Flex, Text, Title } from '@upsales/components';
import formatWidgetValue from '../formatWidgetValue';
import BemClass from '@upsales/components/Utils/bemClass';
import './TotalSalesWithDistribution.scss';
import DistributionBar from '../Components/DistributionBar';
import { InfoColumn } from '../DistributionBarWidget/DistributionBarWidget';

type DistributionBarProps = {
	id: string;
	label: string;
	tooltip?: string;
	count?: number;
	value: number;
	color: Colors;
}[];

const TotalSalesWithDistribution = ({ data, ...props }: GenericWidgetProps) => {
	const classes = new BemClass('TotalSalesWithDistribution');
	const datatype = data.datatype?.progress;
	const progress = data.total?.progress ?? 0;
	const trend = data.total?.special?.trend ?? data.total?.special?.negativeTrend ?? null;
	const hasChip = !!data.total?.special?.trend || !!data.total?.special?.negativeTrend;
	const distribution = (data.total?.special?.distribution as DistributionBarProps) || [];
	const distributionLost = (data.total?.special?.distributionLost as DistributionBarProps) || [];
	const salesChange = data.total?.special?.salesChange ?? 0;
	const currency = data.currency;
	const trendGrew = trend > 0;
	const { config } = props;
	const totalValue = distribution?.length ? distribution.reduce((acc, entry) => acc + entry.value, 0) : 0;
	const totalValueLost = distributionLost?.length ? distributionLost.reduce((acc, entry) => acc + entry.value, 0) : 0;
	const isRecurring = config.valueType === 'recurring';

	return (
		<Flex className={classes.b()} direction="column" space="plxl pbxl prxl" gap={isRecurring ? '' : 'u4'}>
			<Block>
				<Flex alignItems="center" gap="u2">
					<Title bold>{formatWidgetValue(progress, datatype, data.currency)}</Title>
					{hasChip ? (
						<AssistChip
							title={`${trendGrew ? '+' : ''}${formatWidgetValue(salesChange, datatype, currency)} (${
								trendGrew ? '+' : ''
							}${trend}%)`}
							type={trendGrew ? 'success' : 'danger'}
						/>
					) : null}
				</Flex>
				{data.total?.special?.avg ? (
					<Text color="grey-11">{`${data.language?.clientAverage}: ${formatWidgetValue(
						data.total?.special?.avg,
						datatype,
						data.currency
					)}`}</Text>
				) : null}
			</Block>
			<Flex gap="u7">
				{totalValue > 0 ? (
					<Flex space="ptl" direction="column" className={classes.elem('distribution').b()}>
						{isRecurring ? (
							<Text bold space="mbs">
								{`${data.language?.increase}: ${formatWidgetValue(totalValue, datatype, currency)}`}
							</Text>
						) : null}
						<DistributionBar
							distribution={distribution}
							datatype={datatype}
							currency={currency}
							enableDrilldown={data.enableDrilldown}
							config={config}
							thick
						/>
						<Block space="mrxl mtm">
							<Flex>
								{distribution?.map(entry => (
									<InfoColumn
										key={entry.id}
										value={formatWidgetValue(entry.value, datatype, currency)}
										label={entry.label}
										tooltip={entry.tooltip}
										classes={classes}
										loading={false}
										dotColor={entry.color}
									/>
								))}
							</Flex>
						</Block>
					</Flex>
				) : null}
				{totalValueLost > 0 ? (
					<Flex space="ptl" direction="column" className={classes.elem('distribution').b()}>
						<Text bold space="mbs">
							{`${data.language?.lost}: -${formatWidgetValue(totalValueLost, datatype, currency)}`}
						</Text>
						<DistributionBar
							distribution={distributionLost}
							datatype={datatype}
							currency={currency}
							enableDrilldown={data.enableDrilldown}
							config={config}
							thick
						/>
						<Block space="mrxl mtm">
							<Flex>
								{distributionLost?.map(entry => (
									<InfoColumn
										key={entry.id}
										value={formatWidgetValue(entry.value, datatype, currency)}
										label={entry.label}
										tooltip={entry.tooltip}
										classes={classes}
										loading={false}
										dotColor={entry.color}
									/>
								))}
							</Flex>
						</Block>
					</Flex>
				) : null}
			</Flex>
		</Flex>
	);
};

export default TotalSalesWithDistribution;
